import {
  AlertDescription,
  CloseButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Crop,
  CropRef,
  FileUploader,
  Modal,
  ModalBody,
  ModalFooterDefault,
  ModalHeaderDefault,
  OnChangeParameters,
  TextLink,
} from 'ui-components';

import {
  ACCEPTED_FILE_FORMATS,
  MAX_FILE_SIZE,
} from './ImageUploader.constants';
import { validator } from './ImageUploader.validate';

export type ImageUploaderProps = {
  isOpen: boolean;
  onSuccess: (
    croppedImage: Blob | undefined,
    data: Omit<ImageUploaderState, 'croppedImage'>,
  ) => void;
  onReset: () => void;
  onClose: () => void;
};

export interface ImageUploaderState {
  coordinates:
    | { x?: number; y?: number; width?: number; height?: number }
    | undefined;
  croppedImage: Blob | undefined;
  dropImage: string | null;
  originalImage: Blob | undefined;
}

export const ImageUploader = ({
  isOpen,
  onSuccess,
  onReset,
  onClose,
}: ImageUploaderProps) => {
  const cropperRef = useRef<CropRef>(null);

  const initialState: ImageUploaderState = {
    coordinates: undefined,
    croppedImage: undefined,
    dropImage: null,
    originalImage: undefined,
  };

  const [images, setImages] = useState<ImageUploaderState>(initialState);
  const [errors, setErrors] = useState<string | undefined>();
  const { onClose: onCloseError } = useDisclosure({ defaultIsOpen: !!errors });

  const onDrop = useCallback((acceptedFiles: any) => {
    setErrors(undefined);
    acceptedFiles.map((file: any) => {
      const reader = new FileReader();

      reader.onload = function (e) {
        const image = e.target ? String(e.target?.result) : null;
        setImages((prevState) => ({ ...prevState, dropImage: image }));
      };

      reader.readAsDataURL(file);
      return file;
    });
  }, []);

  const handleReset = () => {
    setImages(initialState);
    onReset();
  };

  const handleUpload = useCallback(() => {
    const { croppedImage, ...rest } = images;
    const coordinates = cropperRef.current?.cropper.getData(true);
    onSuccess(croppedImage, { ...rest, coordinates });
    setImages({ ...initialState, coordinates });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.croppedImage]);

  const handleCropChange = useCallback(
    ({
      croppedImageBlob,
      coordinates,
      originalImageBlob,
    }: OnChangeParameters) => {
      if (croppedImageBlob) {
        setImages((prevState) => ({
          ...prevState,
          coordinates: coordinates ?? undefined,
          croppedImage: croppedImageBlob,
          originalImage: originalImageBlob ?? undefined,
        }));
      }
    },
    [],
  );

  const handleClose = useCallback(() => {
    onClose();
    if (images.dropImage) setImages(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, setImages, images.dropImage]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      closeOnOverlayClick={false}
    >
      <ModalHeaderDefault title="Upload photo" />
      <ModalBody>
        {errors ? (
          <Alert
            mb={2}
            status="error"
            display="flex"
            justifyContent="space-between"
          >
            <AlertDescription>{errors}</AlertDescription>
            <CloseButton
              position="relative"
              onClick={() => {
                onCloseError();
                setErrors(undefined);
              }}
            />
          </Alert>
        ) : null}
        {!images.dropImage ? (
          <FileUploader
            accept={ACCEPTED_FILE_FORMATS}
            buttonLabel="Browse"
            descriptionLineOne="Accepted Image Formats: JPG, PNG, GIF"
            descriptionLineTwo="Maximum File Size: 5 MB (megabytes)"
            isLoading={false}
            maxSize={MAX_FILE_SIZE}
            onDrop={onDrop}
            text="Drag and Drop"
            validator={validator}
          />
        ) : (
          <Crop
            imageUrl={images.dropImage}
            isSquare
            onChange={handleCropChange}
            ref={cropperRef}
          />
        )}
        {images.dropImage && (
          <Text display="flex" alignItems="center" marginTop={1} fontSize="sm">
            Any problems?&nbsp;
            <TextLink as="button" onClick={handleReset} size="sm">
              Restore default image
            </TextLink>
          </Text>
        )}
      </ModalBody>
      <ModalFooterDefault
        secondary={{
          title: 'Cancel',
          onClick: handleClose,
        }}
        primary={{
          title: 'Save',
          onClick: handleUpload,
          isDisabled: !images.croppedImage,
        }}
      />
    </Modal>
  );
};
